/**
 * A grid is printed only when its columns are SHARED by every row (review
 * item 7, measured on Clinical Dev 2026-10-17).
 *
 * THE MODEL. A grid section is LONG: each column is ONE variable answered once
 * for every pre-printed row (LBORRES on every analyte row). The renderer
 * crosses every row with every column by construction, and the data
 * dictionary stamps every row on every column variable.
 *
 * THE DEFECT. The variables pass makes WIDE variables, one per item and
 * attribute (TSH, TSHORNLO, TSHORNHI, TSHCLSIG, FT4, ...), and the structure
 * pass put them all into one grid, so the TSH row printed an FT4 cell.
 *
 * THE RULE: WHEN UNSURE, KEEP THE GRID. A grid is demoted to a question list
 * only on an unambiguous structural signal about its ROWS (Jim's Stage 1: the
 * columns are per-item questions, "which shows when a row points at one of
 * the column questions"). Demoting a genuine grid drops its row list, which IS
 * the form's clinical content: a shorter, wrong form is worse than a printed
 * cross product, which is confusing but complete. A grid is demoted when:
 *  - row-is-column: a row's own variable is one of the grid's columns.
 *  - row-owns-columns: a row's own variable is the stem of at least TWO column variables
 *    (row RKEEXT, columns RKEEXT1, RKEEXT2, ...).
 *  - asked-elsewhere: a ROW's own variable is asked in another section of the
 *    form (the vital-signs grid whose rows repeat a question list). A COLUMN
 *    variable shared with another section is NOT a signal: two lab grids may
 *    both use LBORRES.
 *  - row-names-column: a row's label IS one of the grid's OWN column questions
 *    (same text or short label, ignoring case and spacing): the rows are the
 *    grid's variables. Other sections are never compared: a row "Weight" in a
 *    repeat-measurement grid beside a "Weight" question is a genuine grid.
 * All that hold are reported.
 *
 * WHAT IT DELIBERATELY DOES NOT GUESS. Answer-kind patterns (a repeated block
 * of columns, two columns on one coded scale) were tried and removed
 * (checker, 2026-10-17): they demote genuine grids such as right eye / left
 * eye, baseline / follow-up, result / retest result, readings in duplicate.
 * So a stored grid whose row variables a fact-base row list had already wiped
 * (labPanels.applyLabRows, before it learned not to) stays as stored; a
 * regeneration keeps the row variables and row-is-column catches it.
 *
 * Demotion is lossless: the same questions, in the same order, under the same
 * title and instructions, each printed once; nothing is removed, merged,
 * renamed or reordered, and no option changes. Only the grid's own fields go
 * (kind, rows, label header, blank rows).
 *
 * A grid whose columns carry a per-cell map (`varByEntity`, the print-time
 * body-system grid of entityAttributeCollapse.ts) is never demoted: each cell
 * already names its own variable.
 *
 * ONE MODULE, EVERY PATH: generation (crf/structureGrids.ts), saveCrfForm,
 * both print paths, the data dictionary and the CRF Builder page
 * (web/src/pages/project/crfLayoutSections imports this file). Pure, with no
 * imports, so the web bundle can take it.
 */

export type GridDemotionReason =
  | 'row-is-column'
  | 'row-owns-columns'
  | 'asked-elsewhere'
  | 'row-names-column';

export interface GridDemotion {
  /** Index of the section in the array given. */
  index: number;
  title: string;
  /** Every rule that fired, in the order listed above. Never empty. */
  reasons: GridDemotionReason[];
  /** One line naming what fired, for logs. */
  detail: string;
}

/** A form variable, as the page and the generator hold it (for sections that name `varNames`). */
export interface GridVariableLike {
  varName: string;
  questionText?: string;
  shortLabel?: string;
  responseType?: string;
  options?: unknown[];
}

interface ColumnFacts {
  variable: string;
  labels: string[];
  cellMapped: boolean;
}

interface SectionFacts {
  title: string;
  isGrid: boolean;
  rows: Array<{ label: string; variable: string }>;
  columns: ColumnFacts[];
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const upper = (v: unknown): string => str(v).trim().toUpperCase();
const norm = (v: unknown): string => str(v).toLowerCase().replace(/\s+/g, ' ').trim();

function factsOf(sections: unknown[], variables?: GridVariableLike[]): SectionFacts[] {
  const byName = new Map<string, GridVariableLike>();
  for (const v of variables || []) if (v && str(v.varName).trim()) byName.set(upper(v.varName), v);
  return sections.map((raw) => {
    const s = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    let columns: ColumnFacts[] = [];
    if (Array.isArray(s.questions)) {
      columns = (s.questions as unknown[]).filter((x) => x && typeof x === 'object').map((x) => {
        const qq = x as Record<string, unknown>;
        const map = qq.varByEntity;
        return {
          variable: upper(qq.variable),
          labels: [norm(qq.text), norm(qq.short_label)].filter(Boolean),
          cellMapped: !!map && typeof map === 'object' && Object.keys(map as object).length > 0,
        };
      });
    } else if (Array.isArray(s.varNames)) {
      columns = (s.varNames as unknown[]).filter((x) => str(x).trim()).map((name) => {
        const v = byName.get(upper(name));
        return {
          variable: upper(name),
          labels: v ? [norm(v.questionText), norm(v.shortLabel)].filter(Boolean) : [],
          cellMapped: false,
        };
      });
    }
    const rows = Array.isArray(s.rowEntities)
      ? (s.rowEntities as unknown[]).filter((r) => r && typeof r === 'object' && norm((r as { label?: unknown }).label))
          .map((r) => ({ label: norm((r as { label?: unknown }).label), variable: upper((r as { variable?: unknown }).variable) }))
      : [];
    return { title: str(s.title), isGrid: s.kind === 'grid', rows, columns };
  });
}

function verdict(all: SectionFacts[], index: number): GridDemotion | null {
  const g = all[index];
  if (!g.isGrid || g.rows.length === 0 || g.columns.length === 0) return null;
  if (g.columns.some((c) => c.cellMapped)) return null;

  const colVars = new Set(g.columns.map((c) => c.variable).filter(Boolean));
  const otherVars = new Set<string>();
  all.forEach((s, i) => {
    if (i === index) return;
    for (const c of s.columns) if (c.variable) otherVars.add(c.variable);
  });
  const ownLabels = new Set(g.columns.flatMap((c) => c.labels));

  const reasons: GridDemotionReason[] = [];
  const detail: string[] = [];
  const fire = (r: GridDemotionReason, found: string[]) => {
    reasons.push(r);
    detail.push(`${r}: ${[...new Set(found)].slice(0, 4).join(', ')}`);
  };

  const rowIsCol = g.rows.filter((r) => r.variable && colVars.has(r.variable)).map((r) => r.variable);
  if (rowIsCol.length) fire('row-is-column', rowIsCol);

  // At least TWO columns must carry the row's stem: one is a coincidence (row
  // PH beside a PHASE column), two or more is the row's own block of fields.
  const owns = g.rows.filter((r) => r.variable.length >= 2
    && g.columns.filter((c) => c.variable.length > r.variable.length && c.variable.startsWith(r.variable)).length >= 2)
    .map((r) => r.variable);
  if (owns.length) fire('row-owns-columns', owns);

  const askedTwice = g.rows.filter((r) => r.variable && otherVars.has(r.variable)).map((r) => r.variable);
  if (askedTwice.length) fire('asked-elsewhere', askedTwice);

  const named = g.rows.filter((r) => ownLabels.has(r.label)).map((r) => r.label);
  if (named.length) fire('row-names-column', named);

  if (!reasons.length) return null;
  return { index, title: g.title, reasons, detail: detail.join(' | ') };
}

/**
 * Why each grid of these sections would be demoted. Sections are either
 * stored or renderer-shaped (`questions`), or the structure pass's reply
 * (`varNames`, read against `variables`). Clean grids and non-grids are absent.
 */
export function explainGridDemotions(sections: unknown, variables?: GridVariableLike[]): GridDemotion[] {
  if (!Array.isArray(sections) || sections.length === 0) return [];
  const facts = factsOf(sections, variables);
  const out: GridDemotion[] = [];
  facts.forEach((_, i) => { const d = verdict(facts, i); if (d) out.push(d); });
  return out;
}

/** A grid section without its grid fields: a question list with everything else kept. */
function demote<S>(section: S): S {
  const { kind: _k, rowEntities: _r, gridLabelHeader: _h, blankRows: _b, ...rest } = section as Record<string, unknown>;
  return rest as S;
}

/**
 * The sections with every grid whose columns are per-item questions demoted to
 * a question list. Returns the SAME array when nothing is demoted, and never
 * modifies its input.
 */
export function normaliseGridSections<S>(sections: S[], variables?: GridVariableLike[]): S[] {
  const demotions = explainGridDemotions(sections, variables);
  if (demotions.length === 0) return sections;
  const at = new Set(demotions.map((d) => d.index));
  return sections.map((s, i) => (at.has(i) ? demote(s) : s));
}
